import React, { useContext, useEffect, useRef, useState } from 'react';

import {
  findNodeHandle,
  NativeModules,
  Platform,
  StyleSheet,
  TouchableHighlight,
  View,
} from 'react-native';
import DyteClient from '@dytesdk/web-core';
import { useDyteSelector } from '@dytesdk/react-native-core';
import { SvgXml } from 'react-native-svg';
import { defaultIconPack, IconPack } from '../lib/icons';
import { DyteI18n, useLanguage } from '../lib/lang';
import DyteText from './DyteText';
import { ScreenCapturePickerView } from '@dyteinternals/react-native-webrtc';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function ({
  meeting,
  iconPack = defaultIconPack,
  size = 'sm',
  variant = 'button',
  t,
}: {
  meeting: DyteClient;
  iconPack?: IconPack;
  size?: 'lg' | 'md' | 'sm' | 'xl';
  variant?: 'button' | 'horizontal';
  t?: DyteI18n;
}) {
  const activeParticipants = useDyteSelector((m) => m.participants.active);
  const screenshareParticipants = activeParticipants
    .toArray()
    .filter((p) => p.screenShareEnabled);
  const canProduceSSInitialValue = () => {
    const { self, meta, stage } = meeting;
    let _canProduceSS = self.permissions.canProduceScreenshare === 'ALLOWED';
    const isWebinar = meta.viewType === 'WEBINAR';
    if (!_canProduceSS && isWebinar) {
      const canRequestVideo =
        self.permissions.canProduceScreenshare === 'CAN_REQUEST';
      // If the has approved request to present
      if (
        canRequestVideo &&
        (stage.status === 'ON_STAGE' ||
          stage.status === 'ACCEPTED_TO_JOIN_STAGE')
      ) {
        _canProduceSS = true;
      }
    }
    if (
      screenshareParticipants.length >=
      meeting.self.permissions.maxScreenShareCount
    )
      _canProduceSS = false;
    return _canProduceSS;
  };
  const { storeStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  const [canProduceSS, setCanProduceSS] = useState(canProduceSSInitialValue());
  const screenCapturePickerViewRef = useRef(null);
  const screenShareEnabled = useDyteSelector((m) => m.self.screenShareEnabled);
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const showScreenRecordPicker = async () => {
    const handle = findNodeHandle(screenCapturePickerViewRef.current);
    await NativeModules.ScreenCapturePickerViewManager.show(handle);
  };
  let buttonHorizontal = false;
  switch (variant) {
    case 'horizontal':
      buttonHorizontal = true;
      break;
  }
  let buttonSize = 25;
  switch (size) {
    case 'md':
      buttonSize = 50;
      break;
    case 'lg':
      buttonSize = 100;
      break;
    case 'xl':
      buttonSize = 150;
      break;
  }
  const toggleScreenShare = async () => {
    const self = meeting.self;
    if (self == null) {
      return;
    }
    if (self.screenShareEnabled) {
      await self.disableScreenShare();
    } else {
      if (Platform.OS === 'ios') await showScreenRecordPicker();
      await self.enableScreenShare();
    }
  };

  useEffect(() => {
    const updateCanProduceSS = () => {
      const { self, meta, stage } = meeting;
      let _canProduceSS = self.permissions.canProduceScreenshare === 'ALLOWED';
      const isWebinar = meta.viewType === 'WEBINAR';
      if (!_canProduceSS && isWebinar) {
        const canRequestVideo =
          self.permissions.canProduceScreenshare === 'CAN_REQUEST';
        // If the has approved request to present
        if (
          canRequestVideo &&
          (stage.status === 'ON_STAGE' ||
            stage.status === 'ACCEPTED_TO_JOIN_STAGE')
        ) {
          _canProduceSS = true;
        }
      }
      if (
        screenshareParticipants.length >=
        meeting.self.permissions.maxScreenShareCount
      )
        _canProduceSS = false;
      setCanProduceSS(_canProduceSS);
    };

    updateCanProduceSS();
    meeting.stage.addListener('stageStatusUpdate', updateCanProduceSS);

    return () => {
      meeting.stage.removeListener('stageStatusUpdate', updateCanProduceSS);
    };
  }, [meeting, screenshareParticipants.length, canProduceSS]);

  const styles = StyleSheet.create({
    panel: {
      flexDirection: 'row',
      width: '100%',
      paddingVertical: 12,
      paddingLeft: 48,
      justifyContent: 'flex-start',
      alignItems: 'center',
    },
    wrapper: { alignItems: 'center', justifyContent: 'center' },
    button: {
      height: buttonSize * 2,
      width: '100%',
      backgroundColor: colors.background[1000],
    },
    text: { paddingHorizontal: 18, color: colors.text },
    requests: {
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.text,
      height: 24,
      width: 24,
      borderRadius: 16,
      backgroundColor: colors.brand[300],
    },
  });
  if (!canProduceSS) return null;
  if (
    meeting.meta.viewType === 'WEBINAR' &&
    meeting.stage.status !== 'ON_STAGE'
  )
    return null;
  return (
    <View style={styles.wrapper}>
      {buttonHorizontal && (
        <TouchableHighlight
          underlayColor={colors.background[800]}
          onPress={toggleScreenShare}
          style={styles.button}
        >
          <View style={styles.panel}>
            <SvgXml
              xml={(screenShareEnabled
                ? iconPack.share_screen_stop
                : iconPack.share_screen_start
              ).replace('currentColor', colors.text)}
              width={25}
              height={25}
            />
            {Platform.OS === 'ios' && (
              <ScreenCapturePickerView ref={screenCapturePickerViewRef} />
            )}
            <DyteText style={styles.text}>
              {screenShareEnabled
                ? t('screenshare.stop')
                : t('screenshare.start')}
            </DyteText>
          </View>
        </TouchableHighlight>
      )}
      {!buttonHorizontal && (
        <TouchableHighlight
          underlayColor={colors.background[800]}
          onPress={toggleScreenShare}
          style={styles.button}
        >
          <View style={styles.panel}>
            <SvgXml
              xml={(screenShareEnabled
                ? iconPack.share_screen_stop
                : iconPack.share_screen_start
              ).replace('currentColor', colors.text)}
              width={25}
              height={25}
            />
            {Platform.OS === 'ios' && (
              <ScreenCapturePickerView ref={screenCapturePickerViewRef} />
            )}
          </View>
        </TouchableHighlight>
      )}
    </View>
  );
}
